import React from 'react';

import { Timestamp } from 'firebase/firestore';

import MenuItem from '@mui/material/MenuItem';

import { aggregateApplies, competitionAppliesToDay, resolveApplicableCompetitionsForDay, resolveCompetitionHoles, resolveDayCoursePar, resolveGameHoles, resolveTeeSnapshotsForDay, resolveUnitPar, resultAppliesToItsDay, selectedDayIndices, type GameHoles } from 'shared/dist/utils/competitionDays';
import { buildPlayerGroupMap } from 'shared/dist/utils/entryUtils';
import { isGroupEntry, isPairEntry } from 'shared/dist/utils/fieldFilter';
import { dayOptionsWithNotPlayingDays, filterPlayingDay, isPlayingDay, notPlayingDayIndices } from 'shared/dist/utils/registrationDayOptions';
import { removePlayerFromTeeSheet, resolveDayIndexForTeeSheet, resolveTeeSheetForDay } from 'shared/dist/utils/teeSheetUtils';
import {
  DEFAULT_TEE_TIME_SPACING,
  resolveCourseTeeTimeSpacing,
  resolveDefaultStartingHole,
  resolveShotgunStartHole,
  roundHoleNumbers,
  resolveStartingHole,
  resolveTeeTimeSpacing,
  isSameTeeSheetTimingBasis,
  renumberStartingHoles,
  resolveTeeSheetTimingBasis,
  retimeTeeTimes,
} from 'shared/dist/utils/teeTiming';
import {
  contiguousSeatPattern,
  deriveScoringUnitShapeForCompetition,
  formatSeatPattern,
  formScoringUnits,
  isPrimitiveDefiningCompetition,
  seatPatternOptions,
  seatPatternsEqual,
} from 'shared/dist/utils/unitFormation';

// Re-exported so other client files (e.g. usePlayingHandicapsByDay.ts, CompetitionEditor.tsx) get
// these through this utility file rather than deep-importing shared/dist directly themselves —
// see this repo's "never import shared/dist in UI components" convention.
export {
  buildPlayerGroupMap,
  competitionAppliesToDay,
  contiguousSeatPattern,
  deriveScoringUnitShapeForCompetition,
  formatSeatPattern,
  formScoringUnits,
  isPrimitiveDefiningCompetition,
  resolveApplicableCompetitionsForDay,
  resolveCompetitionHoles,
  resolveDayCoursePar,
  resolveDayIndexForTeeSheet,
  resolveGameHoles,
  resolveTeeSnapshotsForDay,
  resolveUnitPar,
  type GameHoles,
  resolveTeeSheetForDay,
  resultAppliesToItsDay,
  aggregateApplies,
  seatPatternOptions,
  seatPatternsEqual,
  selectedDayIndices,
  isGroupEntry,
  isPairEntry,
};

// Which day indices a competition applies to — its own selection when set, otherwise every golf
// day (the existing "unset = all days" convention EventScoreEntry.tsx's dayCompetitions resolution
// and EventGamesView.tsx's compSortKey already both rely on independently). A social (isGolf ===
// false) day never has scores or games, so it's never part of the implicit "all days" default —
// only an explicit dayIndices selection could name one, and the CompetitionEditor day picker
// doesn't offer social days as an option in the first place.
export function dayIndicesForCompetition(competition: Pick<Competition, 'dayIndices' | 'dayIds'>, days: EventDay[]): number[] {
  const selected = selectedDayIndices(competition, days);
  if (selected) return selected;
  return days.reduce<number[]>((acc, day, i) => {
    if (day.isGolf !== false) acc.push(i);
    return acc;
  }, []);
}

/**
 * The holes a game's hole picker offers, for the golf days it's played on. Holes are stored by
 * position in the round (Competition.holes, measurementsByHole), so on 9-hole days only positions
 * 1–9 exist; each is labelled with its real hole number — 10–18 when every one of those days plays
 * the back nine. Days that mix front and back nines, or no days known, keep position labels.
 */
export function holePickerChoices(days: EventDay[]): { position: number; holeNumber: number }[] {
  const allNine = days.length > 0 && days.every((d) => (d.numHoles ?? 18) === 9);
  if (!allNine) return Array.from({ length: 18 }, (_, i) => ({ position: i + 1, holeNumber: i + 1 }));
  const allBack = days.every((d) => d.which9 === 'back');
  return Array.from({ length: 9 }, (_, i) => ({ position: i + 1, holeNumber: (allBack ? 10 : 1) + i }));
}

import { GroupingSlot } from '../components/grouping/GroupingSelector';
import EventRosterInterface from '../interfaces/EventRosterInterface';
import colorWheel from '../styles/colorWheel';
import Competition from '../types/Competition';
import { CoursePair, CourseTee } from '../types/Course';
import Event, { ATTENDEE_OPTIONS, EVENT_TEAM_DEFINED_BY_TYPES, PARTICIPANT_ORDERS } from '../types/Event';
import EventDay, { TEE_SHEET_STATUSES } from '../types/EventDay';
import Grouping, { GROUPING_STATUSES } from '../types/Grouping';
import Registration from '../types/Registration';

import { BLIND_PARTNER_ID, partitionIntoBlindCohorts, resolvePlacedBlinds } from './blindUtils';
import { eventPartnershipSize, getPartnershipDefinedBy, isPartnerEditableEvent, isPartnershipEvent } from './eventTeamUtils';
import { computeTieCluster, resolveGhinTieOrder, TieCluster } from './ghinTie';

// Returns the tee whose total yardage is closest to the target.
// Men's default target: 6000, Women's default target: 5000.
export function findClosestTee(tees: CourseTee[], targetYardage: number): CourseTee | undefined {
  if (!tees.length) return undefined;
  return tees.reduce((best, tee) => {
    const total = tee.yardage.reduce((s, y) => s + y, 0);
    const bestTotal = best.yardage.reduce((s, y) => s + y, 0);
    return Math.abs(total - targetYardage) < Math.abs(bestTotal - targetYardage) ? tee : best;
  });
}

// Tees rated for the given gender. Unknown gender gets every tee.
export function filterTeesForGender(tees: CourseTee[], gender?: Registration['gender']): CourseTee[] {
  if (gender === 'female') return tees.filter(t => t.isForWomen !== false);
  if (gender === 'male') return tees.filter(t => t.isForMen !== false);
  return tees;
}

export function getGolfEventTimezone(day: EventDay, coursePairs: CoursePair[]): string {
  return (
    coursePairs?.find((coursePair) => coursePair.id === day.courseId)?.timezone ??
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
}

/** A group's tee time: an admin-set override if present, else the day's start time plus one
 *  spacing per group (see resolveTeeTimeSpacing). On a shotgun day every group starts at once. */
export function resolveTeeTime(
  day: EventDay,
  group: number,
  teeTimeOverrides: Record<string, Timestamp> | undefined,
  coursePairs: CoursePair[] | undefined,
): Timestamp {
  const override = teeTimeOverrides?.[group.toString()];
  if (override) return override;
  const offsetMinutes = day.isShotgun ? 0 : group * resolveTeeTimeSpacing(day, coursePairs);
  return Timestamp.fromMillis((day.datetime as Timestamp).toMillis() + offsetMinutes * 60000);
}

/**
 * Whether a round's tee sheet is behind us, so a multi-day event can open with it collapsed and the
 * current round on top. A round is past once its date in the event's timezone is before today, or
 * once a later golf round has started — never the last round, which stays current after the event.
 */
export function isTeeSheetRoundPast(days: EventDay[], dayIndex: number, tz: string, nowMs: number = Date.now()): boolean {
  const day = days[dayIndex];
  if (!(day?.datetime instanceof Timestamp)) return false;
  // en-CA formats as YYYY-MM-DD, which compares as text; Intl needs no dayjs plugin set up
  const dateKey = (ms: number) => new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(ms));
  if (dateKey(day.datetime.toMillis()) < dateKey(nowMs)) return true;
  return days.some((later, i) => i > dayIndex && later.isGolf !== false
    && later.datetime instanceof Timestamp && later.datetime.toMillis() <= nowMs);
}

export function getEventRoundTitle(days: EventDay[], dayIndex: number): string {
  const day = days[dayIndex];
  if (day.isGolf === false) return day.socialName?.trim() || 'Social';
  // Calculate round number by counting only non-practice golf rounds up to this point
  const roundNumber = days.slice(0, dayIndex + 1).filter((d) => d.isGolf !== false && !d.isPractice).length;
  return day.isPractice ? 'Practice' : `Day ${roundNumber}`;
}

export function getLocationName(event: Event): string {
  if (event.isGolf) {
    if (event.days.length > 1 && !event.days.every((val, i, arr) => val.courseId === arr[0].courseId)) {
      return 'Multiple Courses';
    } else {
      return event.coursePairs?.find((c) => c.id === event.days[0]?.courseId)?.name ?? '';
    }
  } else {
    if (event.days.length > 1 && !event.days.every((val, i, arr) => val.socialLocation === arr[0].socialLocation)) {
      return 'Multiple Locations';
    } else {
      return event.days[0]?.socialLocation ?? 'TBD';
    }
  }
}

export function formatEventDate(event: Event): string {
  if (event.startDatetime instanceof Timestamp) {
    const date = event.startDatetime.toDate();
    const month = date.toLocaleDateString(undefined, { month: 'long' });
    const day = date.getDate();
    const weekday = date.toLocaleDateString(undefined, { weekday: 'short' });
    return `${month} ${day} (${weekday})`;
  }
  return '';
}

// Tee Sheet utilities
export interface TeeSheetPosition {
  day: number;
  group: number;
  position: number;
  regId: string | null;
  blindLabel?: string;     // "[Blind N]" placeholder label, present only when this slot has a blind placed
  blindPlayerId?: string;  // resolved borrowed playerId, once the admin has picked who it is
}

// Converts this client's day-annotated TeeSheetPosition[] into the raw "{group}-{position}" ->
// playerId slot-occupancy shape TeeSheet.pairings actually persists (and every
// shared/utils/entryUtils position helper, plus formScoringUnits below, parses) — the one place
// that conversion happens, so callers needing a filtered subset of positions (e.g.
// usePlayingHandicapsByDay only wants players it has handicap data for) pass their own `filter`
// rather than each hand-rolling the same reduce. Named for what this is — raw seat assignments,
// no unit/pair/group implied yet — rather than "pairings", which reads as already-formed scoring
// pairs once it sits next to formScoringUnits/ScoringUnit; that's a later, separate step.
export function teeSheetSlotAssignmentsForDay(
  teeSheetPositions: TeeSheetPosition[],
  dayIndex: number,
  filter?: (pos: TeeSheetPosition) => boolean,
): Record<string, string> {
  const slotAssignments: Record<string, string> = {};
  for (const pos of teeSheetPositions) {
    if (pos.day !== dayIndex || !pos.regId) continue;
    if (filter && !filter(pos)) continue;
    slotAssignments[`${pos.group}-${pos.position}`] = pos.regId;
  }
  return slotAssignments;
}

export function getPartnershipNumberMap(partnerships: Registration[]): Record<string, number> {
  // Create a map of partnership groups
  const partnershipGroups = new Map<string, Set<string>>();

  // First pass: group players by their partnerships
  partnerships.forEach((reg) => {
    if (reg.partnerIds?.length) {
      // Create a stable key for the partnership by sorting all IDs
      const allIds = [reg.playerId, ...reg.partnerIds].sort();
      const partnershipKey = allIds.join('-');

      if (!partnershipGroups.has(partnershipKey)) {
        partnershipGroups.set(partnershipKey, new Set(allIds));
      }
    }
  });

  // Convert groups to array and sort by the first player's name for stability
  const sortedGroups = Array.from(partnershipGroups.entries()).sort(([keyA], [keyB]) => {
    const [playerIdA] = keyA.split('-');
    const [playerIdB] = keyB.split('-');
    const playerA = partnerships.find((p) => p.playerId === playerIdA);
    const playerB = partnerships.find((p) => p.playerId === playerIdB);
    return (playerA?.playerName || '').localeCompare(playerB?.playerName || '');
  });

  // Create the final mapping
  const partnershipNumberMap: Record<string, number> = {};
  sortedGroups.forEach(([, group], index) => {
    group.forEach((playerId) => {
      partnershipNumberMap[playerId] = index + 1;
    });
  });

  return partnershipNumberMap;
}

/**
 * True only when both registrations' partnerIds reciprocally name each other -- a confirmed
 * partnership, not merely a one-directional invite that hasn't been accepted yet.
 */
export function arePartnersConfirmed(a: Registration, b: Registration): boolean {
  return !!a.partnerIds?.includes(b.playerId) && !!b.partnerIds?.includes(a.playerId);
}

/**
 * Splits a rank-ordered list of registrations into attendees/waitlist against `cap`, treating a
 * partnership (resolved partner or not -- see below) as one unit that must fit together -- if it
 * can't fit in the remaining capacity, the whole unit waitlists rather than splitting the
 * partnership across the cutoff, and a smaller later unit can backfill the slots it left.
 *
 * Each unit consumes one slot per actual registration in it -- the same per-person count as
 * event.registrationCount and the server's computeFcfsRosterStatus. A player who registered
 * alone (no partner yet, or a partner who hasn't registered) takes one slot, not a whole
 * partnership's worth; an empty partner slot is never charged against maxPlayers.
 */
export function splitByCapacityKeepingPartnersTogether(
  sorted: Registration[],
  cap: number,
): { attendees: Registration[]; waitList: Registration[] } {
  const byPlayerId = new Map(sorted.map((r) => [r.playerId, r]));
  const processed = new Set<string>();
  const units: Registration[][] = [];

  sorted.forEach((reg) => {
    if (processed.has(reg.playerId)) return;
    processed.add(reg.playerId);
    // Combine with a partner whenever one is resolvable in the pool -- confirmed or not, since an
    // unconfirmed pair is still ultimately one partnership's worth of capacity, not two.
    const partnerReg = reg.partnerIds?.[0] ? byPlayerId.get(reg.partnerIds[0]) : undefined;
    if (partnerReg && !processed.has(partnerReg.playerId)) {
      processed.add(partnerReg.playerId);
      units.push([reg, partnerReg]);
    } else {
      units.push([reg]);
    }
  });

  const attendees: Registration[] = [];
  const waitList: Registration[] = [];
  let remaining = cap;
  units.forEach((unit) => {
    if (unit.length <= remaining) {
      attendees.push(...unit);
      remaining -= unit.length;
    } else {
      waitList.push(...unit);
    }
  });

  return { attendees, waitList };
}

export interface PartnershipGroup {
  // Ordered: the display "primary" first (member preferred over guest, otherwise whoever
  // appears first in `regs`), then each partner. Length 1 means no partner.
  members: Registration[];
}

/**
 * Groups a rank-ordered registration list into partnership groups, generalizing the star
 * topology already used for partnerIds -- a group's primary lists every other member in its own
 * partnerIds; a member is a group's primary only if no earlier-processed registration already
 * claimed it as a partner. When `isPartnershipEnabled` is false every registration becomes its
 * own 1-member group (used for events that were switched away from a partnership format --
 * partnerIds/partnerNames are left in place on old registrations rather than cleared, so grouping
 * must be gated on the event's current format, not on whether that stale data still exists).
 */
export function groupRegistrationsByPartnership(
  regs: Registration[],
  isPartnershipEnabled: boolean,
): PartnershipGroup[] {
  const groups: PartnershipGroup[] = [];
  const processedIds = new Set<string>();

  regs.forEach((reg) => {
    if (processedIds.has(reg.playerId)) return;

    if (!isPartnershipEnabled) {
      groups.push({ members: [reg] });
      processedIds.add(reg.playerId);
      return;
    }

    const partners = (reg.partnerIds ?? [])
      .map((partnerId) => regs.find((r) => r.playerId === partnerId))
      .filter((p): p is Registration => p !== undefined);
    const allInPartnership = [reg, ...partners];
    const members = allInPartnership.filter((p) => !p.isGuest);
    const primary = members.length > 0 ? members[0] : allInPartnership[0];

    // Only emit when we've reached the primary, so each group appears once regardless of which
    // member `regs` visits first.
    if (reg.playerId !== primary.playerId) return;

    groups.push({ members: allInPartnership });
    allInPartnership.forEach((p) => processedIds.add(p.playerId));
  });

  return groups;
}

/**
 * True when `member` (a non-primary group member) hasn't reciprocated the primary's partnerIds
 * link yet -- i.e. an invitation the member hasn't accepted. The primary itself is never pending:
 * groupRegistrationsByPartnership only assigns the primary role to whoever already lists every
 * other member in the group, so the primary's own link is confirmed by construction.
 */
export function isPartnerRowPending(member: Registration, primary: Registration): boolean {
  if (member.playerId === primary.playerId) return false;
  return !member.partnerIds?.includes(primary.playerId);
}

export interface ResolvedPartnershipGroup {
  primary: Registration;
  primaryCount: number;
  // A partner may already have a registration (confirmed or pending on their end), or may only
  // exist as an invite on the primary's own record (they haven't registered at all yet, so
  // there's no row for them). partnerCount is set whenever either applies.
  resolvedPartner?: Registration;
  partnerCount?: number;
  invitedOnlyName?: string;
  // The pending partner's playerId, whichever way they're known -- resolvedPartner.playerId when
  // they have a registration, otherwise the id the primary invited (primary.partnerIds[0]). Lets
  // a caller check "is the viewer themselves the one who's pending" without caring which case
  // applies. Undefined whenever hasPartnerSlot is false or the partner is already confirmed.
  pendingPlayerId?: string;
  hasPartnerSlot: boolean;
  isPartnerPending: boolean;
  // True when the "partner" slot is a declared blind (Registration.partnerIds === [BLIND_PARTNER_ID])
  // rather than an actual pending invite — displayed the same "no resolved registration" way as an
  // unaccepted guest invite (invitedOnlyName carries "[Blind]"), but it needs no acceptance from
  // anyone and isn't waiting on a person, so callers must not label it "Pending acceptance".
  isBlindPartner: boolean;
  // Undefined when there's no partner slot at all -- no partnership number has been assigned yet.
  railColor?: string;
  // True only when reserveEmptySlots is true and this primary is partnership-eligible but has no
  // partner or invite yet -- the caller decides how (or whether) to render that empty slot; e.g.
  // only an editable list offers an "Add partner" action there. Never numbered: positions count
  // registrations only, matching event.registrationCount and maxPlayers capacity.
  hasEmptySlot: boolean;
}

/**
 * Single source of truth for how a partnership-grouped attendee list numbers, colors, and
 * classifies its rows -- shared by every list that renders groupRegistrationsByPartnership's
 * output (roster view, roster editor, waitlist), so the same partnership always numbers, colors,
 * and shows pending/empty state identically everywhere it appears.
 */
export function resolvePartnershipGroupsForDisplay(
  groups: Registration[][],
  partnershipNumberMap: Record<string, number>,
  options: { eventInfo: EventRosterInterface; reserveEmptySlots?: boolean },
): ResolvedPartnershipGroup[] {
  const { eventInfo, reserveEmptySlots = false } = options;

  let runningCount = 0;
  return groups.map((group) => {
    const primary = group[0];
    const resolvedPartner = group[1];
    const invitedOnlyName = !resolvedPartner ? primary.partnerNames?.[0] : undefined;
    const hasPartnerSlot = !!resolvedPartner || !!invitedOnlyName;
    const isBlindPartner = !resolvedPartner && primary.partnerIds?.[0] === BLIND_PARTNER_ID;
    const isPartnerPending = resolvedPartner ? isPartnerRowPending(resolvedPartner, primary) : true;
    const pendingPlayerId = hasPartnerSlot && isPartnerPending
      ? (resolvedPartner?.playerId ?? primary.partnerIds?.[0])
      : undefined;
    const railColor = hasPartnerSlot
      ? colorWheel[((partnershipNumberMap[primary.playerId] ?? 1) - 1) % colorWheel.length]?.[0]
      : undefined;

    runningCount += 1;
    const primaryCount = runningCount;

    // Only a partner with an actual registration takes a numbered position -- an invited-only
    // partner who hasn't registered yet (or a declared blind) doesn't count toward
    // registrationCount, so numbering them would push the visible count past maxPlayers.
    let partnerCount: number | undefined;
    if (resolvedPartner) {
      runningCount += 1;
      partnerCount = runningCount;
    }
    // isPartnerEditableEvent excludes formal LEAGUE team events (partnerIds there, if set at all,
    // names an in-team "game partner" for scoring, not the roster team itself) and PAIRING-defined
    // events (partnerIds there is system-derived from tee sheet groupings, not something to
    // manually "add") -- see its doc comment for why this must stay one function.
    const hasEmptySlot = !hasPartnerSlot && reserveEmptySlots && isPartnerEditableEvent(eventInfo) && !primary.isGuest;

    return {
      primary, primaryCount, resolvedPartner, partnerCount, invitedOnlyName, pendingPlayerId,
      hasPartnerSlot, isPartnerPending, isBlindPartner, railColor, hasEmptySlot,
    };
  });
}

export const sortRegistrations = (
  a: Registration,
  b: Registration,
  regs: Registration[],
  isPartnershipEvent?: boolean
): number => {
  // For individual events, just sort alphabetically
  if (!isPartnershipEvent) {
    return a.playerName.localeCompare(b.playerName);
  }

  // For team events, sort by partnership number first, then alphabetically
  const partnershipMap = getPartnershipNumberMap(regs);
  const aNumber = partnershipMap[a.playerId] || Number.MAX_SAFE_INTEGER;
  const bNumber = partnershipMap[b.playerId] || Number.MAX_SAFE_INTEGER;

  if (aNumber !== bNumber) {
    return aNumber - bNumber;
  }

  // If in same partnership group, sort by player name
  return a.playerName.localeCompare(b.playerName);
};

// Attendee List calculations
export type AttendeeSortOption = 'lastName' | 'firstName' | 'signupTime' | 'ghinIndex';

// Groups registrations by teamId and splits into attendees/waitList per team.
// Pass null for cap to put everyone in attendees (used for MANAGER_SELECTS mode).
// detectTies must only be true when `regs` is actually GHIN-sorted (GHIN_INDEX participant
// order) — otherwise a boundary's neighboring ghinIndex values are meaningless and checking them
// for equality would surface spurious "ties" that have nothing to do with the real cutoff.
function buildTeamRosterMap(
  regs: Registration[],
  cap: number | null,
  detectTies: boolean = false,
): Record<string, TeamRosterEntry> {
  const map: Record<string, TeamRosterEntry> = {};
  regs.forEach((reg) => {
    if (!reg.teamId) return;
    if (!map[reg.teamId]) {
      map[reg.teamId] = { attendees: [], waitList: [], tieCluster: null };
    }
    const entry = map[reg.teamId];
    if (cap === null || entry.attendees.length < cap) {
      entry.attendees.push(reg);
    } else {
      entry.waitList.push(reg);
    }
  });
  if (detectTies && cap !== null && cap > 0) {
    Object.values(map).forEach((entry) => {
      entry.tieCluster = computeTieCluster([...entry.attendees, ...entry.waitList], cap);
    });
  }
  return map;
}

export interface TeamRosterEntry {
  attendees: Registration[];
  waitList: Registration[];
  // Set only when this team's roster cutoff falls in the middle of a GHIN-index tie.
  tieCluster: TieCluster<Registration> | null;
}

export interface AttendeeListResult {
  attendeeList: Registration[];
  waitList: Registration[];
  // Per-league-team breakdown for team events with maxPlayersPerTeam set.
  // Key is teamId; players without a team are excluded from this map.
  teamRosterMap: Record<string, TeamRosterEntry>;
}

/**
 * Computes the whole-event GHIN-index tie cluster for non-team events (no per-team cap exists,
 * so the tie — if any — is at the whole-event maxPlayers cutoff instead). Team events' ties are
 * already available per-team via TeamRosterEntry.tieCluster in calculateAttendeeAndWaitLists's
 * teamRosterMap; this only covers the case that map can't express.
 */
export function computeEventLevelTieCluster(
  regs: Registration[] | null,
  eventInfo: Pick<EventRosterInterface, 'isTeamEvent' | 'isPartnershipEvent' | 'participantOrder' | 'maxPlayers'>
): TieCluster<Registration> | null {
  if (!regs || isPartnershipEvent(eventInfo) || eventInfo.participantOrder !== PARTICIPANT_ORDERS.GHIN_INDEX) return null;
  const cap = eventInfo.maxPlayers;
  if (!cap || cap === -1) return null;
  return computeTieCluster(resolveGhinTieOrder(regs), cap);
}

export function sortRegistrationsByOption(list: Registration[], currentSort: AttendeeSortOption): Registration[] {
  switch (currentSort) {
    case 'lastName':
      return [...list].sort((a, b) => {
        const aParts = a.playerName.trim().split(' ').filter((p) => p.length > 0);
        const bParts = b.playerName.trim().split(' ').filter((p) => p.length > 0);
        const aLast = aParts.length > 0 ? aParts[aParts.length - 1] : '';
        const bLast = bParts.length > 0 ? bParts[bParts.length - 1] : '';
        const cmp = aLast.localeCompare(bLast);
        if (cmp !== 0) return cmp;
        return (aParts[0] ?? '').localeCompare(bParts[0] ?? '');
      });
    case 'firstName':
      return [...list].sort((a, b) => {
        const aParts = a.playerName.trim().split(' ').filter((p) => p.length > 0);
        const bParts = b.playerName.trim().split(' ').filter((p) => p.length > 0);
        const cmp = (aParts[0] ?? '').localeCompare(bParts[0] ?? '');
        if (cmp !== 0) return cmp;
        const aLast = aParts.length > 0 ? aParts[aParts.length - 1] : '';
        const bLast = bParts.length > 0 ? bParts[bParts.length - 1] : '';
        return aLast.localeCompare(bLast);
      });
    case 'signupTime':
      return list;
    case 'ghinIndex':
      return resolveGhinTieOrder(list);
    default:
      return list;
  }
}

export function calculateAttendeeAndWaitLists(
  regs: Registration[] | null,
  eventInfo: EventRosterInterface,
  currentSort: AttendeeSortOption
): AttendeeListResult {
  const empty: AttendeeListResult = { attendeeList: [], waitList: [], teamRosterMap: {} };
  if (!regs) return empty;

  // For MANAGER_SELECTS mode all registrants appear as attendees — manager drives selection via isManagerSelected.
  // Per-team roster map still shows who is confirmed vs pending for each team.
  if (eventInfo.participantOrder === PARTICIPANT_ORDERS.MGR_CHOICE) {
    const teamRosterMap = buildTeamRosterMap(regs, null);
    return { attendeeList: regs, waitList: [], teamRosterMap };
  }

  // First, determine attendeeList and waitList based on event's participantOrder
  let sortedForWaitlist: Registration[];

  if (eventInfo.participantOrder === PARTICIPANT_ORDERS.GHIN_INDEX) {
    // Sort by GHIN index for waitlist determination; tieRank/tieBreaker resolve exact index ties
    sortedForWaitlist = resolveGhinTieOrder(regs);
  } else if (eventInfo.participantOrder === PARTICIPANT_ORDERS.DRAWING) {
    // Deterministic random draw: sort by the stable tieBreaker value assigned at registration
    sortedForWaitlist = [...regs].sort((a, b) => (a.tieBreaker ?? 0.5) - (b.tieBreaker ?? 0.5));
  } else {
    // Sort by signup time for waitlist determination (regs are already in signup order)
    sortedForWaitlist = [...regs];
  }

  // Apply per-team cap first (SELF_SIGNUP with maxPlayersPerTeam)
  let teamRosterMap: Record<string, TeamRosterEntry> = {};
  const perTeamWaitlisted = new Set<string>();

  const isGhinSorted = eventInfo.participantOrder === PARTICIPANT_ORDERS.GHIN_INDEX;
  const teamCap = isPartnershipEvent(eventInfo) ? eventPartnershipSize(eventInfo) : undefined;
  if (teamCap && teamCap > 0) {
    teamRosterMap = buildTeamRosterMap(sortedForWaitlist, teamCap, isGhinSorted);
    // Collect player IDs that are per-team waitlisted
    Object.values(teamRosterMap).forEach((entry) => {
      entry.waitList.forEach((reg) => perTeamWaitlisted.add(reg.playerId));
    });
  } else if (isPartnershipEvent(eventInfo)) {
    // Team event without a per-team cap: build map with no cap (all players in attendees)
    teamRosterMap = buildTeamRosterMap(sortedForWaitlist, null, isGhinSorted);
  }

  // Apply global maxPlayers logic to determine who gets in vs waitlist
  // Players already per-team-waitlisted are excluded from the global attendee pool.
  const eligibleForGlobal = sortedForWaitlist.filter((r) => !perTeamWaitlisted.has(r.playerId));

  let attendeeList: Registration[];
  let waitList: Registration[];

  const globalCap = eventInfo.maxPlayers;
  // Formal league-team events (pre-existing league teams, not an ad hoc player/admin-formed
  // partnership) have capacity governed entirely by teamStructure.size per team, via
  // buildTeamRosterMap above — matching the server's own computeAndWriteRosterStatus, which never
  // consults maxPlayers for team events at all. Layering a second, flat, whole-event cap on top
  // here would silently waitlist real teammates who are well within their own team's roster size
  // whenever maxPlayers happens to hold a stray/unrelated value — and since a flat-waitlisted
  // (non-follower) player isn't re-included by the tee-sheet-eligible list downstream, they'd
  // vanish from the tee sheet entirely rather than just showing up on a waitlist. Scoped to LEAGUE
  // specifically (not just any teamCap>0) because a PLAYER/ADMIN-defined partnership can share the
  // same teamStructure.size field without registrations ever getting a teamId — buildTeamRosterMap
  // silently skips those, so their capacity genuinely is enforced by the flat-cap branch below.
  const isLeagueTeamEvent = isPartnershipEvent(eventInfo) && getPartnershipDefinedBy(eventInfo) === EVENT_TEAM_DEFINED_BY_TYPES.LEAGUE;

  if (isLeagueTeamEvent || !globalCap || globalCap === -1) {
    // No global limit, or a league-team event (see comment above): everyone eligible (already
    // narrowed by the per-team cap when applicable) is an attendee.
    attendeeList = eligibleForGlobal;
    waitList = [];
  } else {
    ({ attendees: attendeeList, waitList } = splitByCapacityKeepingPartnersTogether(
      eligibleForGlobal, globalCap
    ));
  }

  // Per-team waitlisted players are appended to the global waitlist after global waitlisters
  const perTeamWaitList = sortedForWaitlist.filter((r) => perTeamWaitlisted.has(r.playerId));
  waitList = [...waitList, ...perTeamWaitList];

  // Follow-the-field: overlay isFollowerConfirmed on every waitlisted registration that requested
  // isFollowingField, live, on every render, for every participant order. For FCFS/team-GHIN-locked
  // events this just re-confirms the server-persisted value (computeAndWriteFollowerStatus); for
  // individual (non-team) GHIN_INDEX/DRAWING — which never persist rosterStatus at all — this is
  // the ONLY place follower status is ever computed. Follower-slot assignment is ALWAYS by signup
  // order (regs is already datetime-ordered from useEventRegs), never by GHIN index/tieBreaker,
  // regardless of how the main competing roster itself is ordered.
  //
  // Gated by the same FOLLOW_FIELD option check computeAndWriteFollowerStatus uses server-side —
  // unlike the FCFS/team-locked classes (which just stop being actively recomputed once the option
  // is disabled, leaving their last-persisted value in place), this live class has no persisted
  // memory at all, so "grandfathering" isn't meaningful here: recomputing to false is the only
  // coherent behavior once the option is off.
  if (eventInfo.attendeeOptions?.includes(ATTENDEE_OPTIONS.FOLLOW_FIELD)) {
    waitList = overlayFollowerStatus(waitList, regs, eventInfo.maxFieldFollowers);
    Object.values(teamRosterMap).forEach((entry) => {
      entry.waitList = overlayFollowerStatus(entry.waitList, regs, eventInfo.maxFieldFollowers);
    });
  }

  return {
    attendeeList: sortRegistrationsByOption(attendeeList, currentSort),
    waitList: sortRegistrationsByOption(waitList, currentSort),
    teamRosterMap,
  };
}

function overlayFollowerStatus(
  waitlistedRegs: Registration[],
  regs: Registration[],
  maxFieldFollowers: number | undefined
): Registration[] {
  const waitlistedIds = new Set(waitlistedRegs.map((r) => r.playerId));
  const followerPool = regs.filter((r) => waitlistedIds.has(r.playerId) && r.isFollowingField === true);
  const hasLimit = typeof maxFieldFollowers === 'number' && maxFieldFollowers > 0;
  const confirmedIds = new Set(
    (hasLimit ? followerPool.slice(0, maxFieldFollowers) : followerPool).map((r) => r.playerId)
  );
  return waitlistedRegs.map((r) => ({ ...r, isFollowerConfirmed: confirmedIds.has(r.playerId) }));
}

// GroupingSlots calculation
export function calculateGroupingSlotsMap(
  groupings: Grouping[] | null,
  attendeeList: Registration[]
): Map<string, GroupingSlot[]> {
  if (!groupings || groupings.length === 0) {
    return new Map<string, GroupingSlot[]>();
  }

  // Find the maximum number of groupings any one person is in
  const playerGroupingCounts = new Map<string, number>();
  groupings.forEach((grouping) => {
    if (grouping.status === GROUPING_STATUSES.ACTIVE) {
      grouping.members.forEach((memberId) => {
        playerGroupingCounts.set(memberId, (playerGroupingCounts.get(memberId) || 0) + 1);
      });
    }
  });

  const maxGroupings = Math.max(...Array.from(playerGroupingCounts.values()), 0);
  if (maxGroupings === 0) {
    return new Map<string, GroupingSlot[]>();
  }

  // Create flightSlots for each attendee
  const slotsMap = new Map<string, GroupingSlot[]>();

  attendeeList.forEach((attendee) => {
    const attendeeSlots: GroupingSlot[] = [];

    // Find groupings that include this attendee
    const attendeeGroupings = groupings.filter(
      (grouping) => grouping.status === GROUPING_STATUSES.ACTIVE && grouping.members.includes(attendee.playerId)
    );

    // Fill in slots with actual groupings
    attendeeGroupings.forEach((grouping, index) => {
      if (index < maxGroupings) {
        attendeeSlots.push({
          symbol: grouping.symbol,
          color: grouping.backgroundColor || 'transparent',
          status: grouping.status,
        });
      }
    });

    // Fill remaining slots with empty slots
    while (attendeeSlots.length < maxGroupings) {
      attendeeSlots.push({
        symbol: '',
        color: 'transparent',
        status: GROUPING_STATUSES.ACTIVE,
      });
    }

    slotsMap.set(attendee.playerId, attendeeSlots);
  });

  return slotsMap;
}

// Tee Sheet Positions calculation
/**
 * Resolves how many position slots one tee sheet group should have. Team matchup groups are
 * always 2-per-team/4-ball, independent of teeSheetGroupSize (see buildAutoFillPairings's own
 * doc comment — that generic per-event setting only governs the non-matchup fill methods, e.g.
 * By Index/Balanced Foursomes/Mixer); using it for a team event would silently shrink the
 * editable grid below what a 2v2 match actually needs, hiding the slot needed to add the 4th
 * player. Also defensively covers `maxOccupiedPosition` (whatever position is actually the
 * highest-filled one in this group) so an already-placed real player can never go invisible just
 * because teeSheetGroupSize shrank out from under them after the fact.
 *
 * Shared by calculateTeeSheetPositions below (reads the persisted/published tee sheet) and
 * useEventTeeSheetState's own position grid (reads the in-progress local edit state) — the two
 * places a tee sheet's position grid gets built, from two different data sources.
 */
export function resolveTeeSheetGroupSize(
  eventInfo: Pick<EventRosterInterface, 'isTeamEvent' | 'isPartnershipEvent' | 'teeSheetGroupSize'>,
  maxOccupiedPosition: number,
): number {
  const configuredGroupSize = isPartnershipEvent(eventInfo) ? 4 : (eventInfo.teeSheetGroupSize ?? 4);
  return Math.max(configuredGroupSize, maxOccupiedPosition + 1);
}

export function calculateTeeSheetPositions(
  eventInfo: EventRosterInterface,
  registrations: Registration[],
  groupings: Grouping[],
): TeeSheetPosition[] {
  const positions: TeeSheetPosition[] = [];
  const playingRegs = registrations.filter((reg) => reg.isPlayingGame !== false);

  eventInfo.days.forEach((day, dayIndex) => {
    // Blinds can only be drawn from players in the field THAT day
    const cohorts = partitionIntoBlindCohorts(filterPlayingDay(playingRegs, day), groupings);
    // Get the published teeSheet for this day
    const publishedTeeSheet = day.teeSheets?.find(ts => ts.status === TEE_SHEET_STATUSES.PUBLISHED) || day.teeSheets?.[0];
    const pairings = publishedTeeSheet?.pairings ?? {};
    const resolvedBlinds = new Map(
      resolvePlacedBlinds(publishedTeeSheet?.blinds ?? {}, pairings, cohorts).map((rb) => [rb.positionKey, rb])
    );

    for (let group = 0; group < eventInfo.numTeeTimes; group++) {
      const maxOccupiedPosition = Object.keys(pairings)
        .filter((key) => key.startsWith(`${group}-`))
        .reduce((max, key) => Math.max(max, parseInt(key.split('-')[1], 10) || 0), -1);
      const groupSize = resolveTeeSheetGroupSize(eventInfo, maxOccupiedPosition);

      for (let position = 0; position < groupSize; position++) {
        const key = `${group}-${position}`;
        const regId = pairings[key] || null;
        const resolvedBlind = resolvedBlinds.get(key);
        positions.push({
          day: dayIndex,
          group,
          position,
          regId,
          ...(resolvedBlind ? { blindLabel: resolvedBlind.label, blindPlayerId: resolvedBlind.resolvedPlayerId } : {}),
        });
      }
    }
  });
  return positions;
}

/** Resolves the display label for a tee sheet position: the real player's name, `[BorrowedName]`
 *  when a placed blind has been resolved, the `[Blind N]` placeholder when placed but unresolved,
 *  or null when the slot is genuinely empty (no real player, no blind — skip it). Shared by every
 *  tee sheet export (CSV, PDF, copy-to-clipboard text) so blind slots render consistently instead
 *  of silently dropping out. */
export function resolveGroupPositionLabel(pos: TeeSheetPosition, regMap: Map<string, Registration>): string | null {
  if (pos.regId) return regMap.get(pos.regId)?.playerName ?? null;
  if (pos.blindPlayerId) {
    const borrowedName = regMap.get(pos.blindPlayerId)?.playerName;
    return borrowedName ? `[${borrowedName}]` : (pos.blindLabel ?? null);
  }
  return pos.blindLabel ?? null;
}

// Get unassigned players - those playing the day but not assigned to any tee sheet position.
// Pass `days` so a player sitting out that day (Registration.dayOptions) is left out too.
export function getUnassignedPlayersForDay(
  teeSheetPositions: TeeSheetPosition[], 
  attendeeList: Registration[], 
  day?: number,
  days?: EventDay[],
): Registration[] {
  const relevantPositions = day !== undefined 
    ? teeSheetPositions.filter((pos) => pos.day === day)
    : teeSheetPositions;
    
  const assignedPlayerIds = new Set(
    relevantPositions
      .filter((pos) => pos.regId)
      .map((pos) => pos.regId)
  );
  
  const eligible = day !== undefined && days ? filterPlayingDay(attendeeList, days[day]) : attendeeList;
  return eligible.filter((reg) => !assignedPlayerIds.has(reg.playerId));
}

// Registered players sitting out the given day (Registration.dayOptions)
export function getNotPlayingPlayersForDay(attendeeList: Registration[], day: EventDay | undefined): Registration[] {
  return attendeeList.filter((reg) => !isPlayingDay(reg, day));
}

export { isPlayingDay, filterPlayingDay, notPlayingDayIndices, dayOptionsWithNotPlayingDays, removePlayerFromTeeSheet };
export {
  DEFAULT_TEE_TIME_SPACING,
  resolveCourseTeeTimeSpacing,
  resolveDefaultStartingHole,
  resolveShotgunStartHole,
  roundHoleNumbers,
  resolveStartingHole,
  resolveTeeTimeSpacing,
  isSameTeeSheetTimingBasis,
  renumberStartingHoles,
  resolveTeeSheetTimingBasis,
  retimeTeeTimes,
};

// Sort attendees
export function sortAttendees(
  attendeeList: Registration[],
  eventInfo: EventRosterInterface
): Registration[] {
  const allRegs = [...attendeeList];
  
  if (eventInfo.participantOrder === PARTICIPANT_ORDERS.GHIN_INDEX) {
    return allRegs.sort((a, b) => {
      // First sort by GHIN index (lower indexes first)
      const aIndex = a.ghinIndex ?? 99;
      const bIndex = b.ghinIndex ?? 99;

      if (aIndex !== bIndex) {
        return aIndex - bIndex;
      }

      // If GHIN indexes are the same, fallback to alphabetical
      return a.playerName.localeCompare(b.playerName);
    });
  }

  // Default sorting (alphabetical or by partnership)
  return allRegs.sort((a, b) => sortRegistrations(a, b, allRegs, isPartnershipEvent(eventInfo)));
}

// Get default sort option for an event
export function getDefaultSortForEvent(eventInfo: EventRosterInterface): AttendeeSortOption {
  return eventInfo.participantOrder === PARTICIPANT_ORDERS.GHIN_INDEX ? 'ghinIndex' : 'firstName';
}

/**
 * Builds the EventRosterInterface used by roster/tee-sheet data hooks from a raw Event doc.
 * Falls back each day's competitions to the event-level list (filtered by dayIndices) when the
 * day doesn't already carry its own — older events only stored competitions at the event level.
 */
export function buildEventRosterInfo(event: Event): EventRosterInterface {
  return {
    orgId: event.orgId,
    leagueId: event.leagueId,
    itemId: event.itemId,
    attendeeOptions: event.attendeeOptions,
    coursePairs: event.coursePairs,
    days: event.days.map((day, dayIdx) => {
      if (day.competitions?.length) return day;
      return { ...day, competitions: resolveApplicableCompetitionsForDay(day, event.competitions, dayIdx) };
    }),
    isGolf: event.isGolf ?? false,
    isTeamEvent: event.isTeamEvent,
    isPartnershipEvent: event.isPartnershipEvent,
    maxPlayers: event.maxPlayers,
    maxFieldFollowers: event.maxFieldFollowers,

    name: event.name,
    leagueName: event.leagueName,
    orgName: event.orgName,
    seasonName: event.seasonName,
    numTeeTimes: event.numTeeTimes,
    teeSheetGroupSize: event.teeSheetGroupSize,
    participantOrder: event.participantOrder,
    rosterSetDeadline: event.rosterSetDeadline ?? event.ghinIndexDate,
    teamRosterDates: event.teamRosterDates,
    teamShowRosterDates: event.teamShowRosterDates,
    showRosterToAllDate: event.showRosterToAllDate,
    showRosterToTeamDate: event.showRosterToTeamDate,
    teamStructure: event.teamStructure,
    partnership: event.partnership,
  };
}

// Check if tee sheets should be displayed instead of attendee list
export function isTeeSheetDisplayed(days: EventDay[]): boolean {
  // Check if at least one day has a published tee sheet
  return days.some(day => 
    day.teeSheets && 
    day.teeSheets.some(teeSheet => teeSheet.status === TEE_SHEET_STATUSES.PUBLISHED)
  );
}

// Action Menu generation
export function generateActionMenu(
  eventInfo: EventRosterInterface,
  regs: Registration[] | null,
  isLeagueAdmin: (leagueId: string, orgId: string) => boolean | undefined,
  handleExportAttendees: () => void,
  EventCopyEmailListComponent: React.ComponentType<{ eventRegs: Registration[] }>
): React.ReactElement[] {
  if (!isLeagueAdmin(eventInfo.leagueId, eventInfo.orgId)) {
    return [];
  }

  const menuItems: React.ReactElement[] = [];

  if (eventInfo.isGolf) {
    menuItems.push(
      React.createElement(MenuItem, {
        key: "export",
        onClick: handleExportAttendees
      }, "Export")
    );
  }

  if (regs) {
    menuItems.push(
      React.createElement(EventCopyEmailListComponent, {
        key: "copy-email",
        eventRegs: regs
      })
    );
  }

  return menuItems;
}

type RosterVisibilityParams = {
  event: Pick<EventRosterInterface, 'showRosterToAllDate' | 'showRosterToTeamDate' | 'teamShowRosterDates' | 'isTeamEvent' | 'isPartnershipEvent' | 'teamStructure' | 'partnership'>;
  isGod?: boolean;
  isAdmin: boolean;
  managedTeams: string[];
  memberTeamIds: string[];
  teamId?: string;
  now?: Date;
};

export function canSeeEventRoster({
  event,
  isGod = false,
  isAdmin,
  managedTeams,
  memberTeamIds,
  teamId,
  now = new Date(),
}: RosterVisibilityParams): boolean {
  if (isGod) return true;

  const isRosterPartnershipEvent = isPartnershipEvent(event);
  const isAnyTeamMember = memberTeamIds.length > 0;

  // Per-team override takes precedence over the event-level date; fall back when absent
  const teamDate = (teamId !== undefined && event.teamShowRosterDates?.[teamId] instanceof Timestamp)
    ? (event.teamShowRosterDates![teamId] as Timestamp).toDate()
    : (event.showRosterToTeamDate instanceof Timestamp ? event.showRosterToTeamDate.toDate() : null);
  const allDate = event.showRosterToAllDate instanceof Timestamp
    ? event.showRosterToAllDate.toDate()
    : null;

  if (teamId !== undefined) {
    // Team captains always see their own team
    if (managedTeams.includes(teamId)) return true;
    // Org/league admins always see all teams
    // TODO: make this optionally gated via event/league/org setting (adminCanSeeAllRosters)
    if (isAdmin) return true;
    // teamDate blocks non-captains, non-admins
    if (teamDate && now < teamDate) return false;
    const isThisTeamMember = memberTeamIds.includes(teamId);
    if (isThisTeamMember) return true;
    // Non-members fall through to allDate
  } else if (isRosterPartnershipEvent) {
    // Admins always see all rosters
    if (isAdmin) return true;
    if (teamDate && now < teamDate) return false;
    if (isAnyTeamMember) return true;
    // Non-members fall through to allDate
  } else {
    // Non-team event: admins always see
    if (isAdmin) return true;
  }

  // Governed by showRosterToAllDate
  if (!allDate) return true;
  return now >= allDate;
}

export type TeamRosterDateInfo = {
  // "Rosters are set on {date}" row
  showRosterSetLine: boolean;
  rosterSetDate: Timestamp | null;     // effective date to display/edit (team override ?? event default)
  rosterSetDeadline: Timestamp | null; // event-level max allowed date for the editor
  // "Visible to members on {date}" / "Roster visible to members" row
  showVisibilityLine: boolean;
  visibilityDate: Timestamp | null;    // effective date to display/edit (team override ?? event default)
  isVisibleToMembers: boolean;         // effective date has passed — roster already visible
  visibilityMaxDate: Timestamp | null; // event-level max allowed date for the editor
};

export function getTeamRosterDateInfo(
  eventInfo: Pick<EventRosterInterface,
    'participantOrder' | 'rosterSetDeadline' | 'teamRosterDates' |
    'showRosterToTeamDate' | 'showRosterToAllDate' | 'teamShowRosterDates'
  >,
  teamId: string,
  canEditTeam: boolean,
  showRoster: boolean,
  now = new Date()
): TeamRosterDateInfo {
  const empty: TeamRosterDateInfo = {
    showRosterSetLine: false, rosterSetDate: null, rosterSetDeadline: null,
    showVisibilityLine: false, visibilityDate: null, isVisibleToMembers: false, visibilityMaxDate: null,
  };

  if (teamId === '__free_agents__') return empty;

  // --- "Rosters are set on {date}" ---
  const rosterSetDeadline = eventInfo.rosterSetDeadline instanceof Timestamp
    ? eventInfo.rosterSetDeadline : null;
  const rosterSetDate = eventInfo.teamRosterDates?.[teamId] ?? rosterSetDeadline;
  const showsRosterDate = (
    eventInfo.participantOrder === PARTICIPANT_ORDERS.GHIN_INDEX ||
    eventInfo.participantOrder === PARTICIPANT_ORDERS.DRAWING ||
    eventInfo.participantOrder === PARTICIPANT_ORDERS.MGR_CHOICE
  );
  const showRosterSetLine = showsRosterDate && !!rosterSetDate && now < rosterSetDate.toDate();

  // --- "Visible to members on {date}" / "Roster visible to members" ---
  const showRosterToTeamDate = eventInfo.showRosterToTeamDate instanceof Timestamp
    ? eventInfo.showRosterToTeamDate : null;
  const visibilityMaxDate = eventInfo.showRosterToAllDate instanceof Timestamp
    ? eventInfo.showRosterToAllDate : null;
  const teamSpecificShowDate = eventInfo.teamShowRosterDates?.[teamId];
  const visibilityDate = teamSpecificShowDate ?? showRosterToTeamDate;
  const isVisibleToMembers = !!visibilityDate && now >= visibilityDate.toDate();

  // Admins/captains see this line for the full window while the event-level date hasn't passed —
  // covers "not yet visible to members" (show date) and "visible early via team override" (show status).
  // Regular members only see it when the roster is hidden and a team-specific date differs from the event default.
  const showVisibilityLine = canEditTeam
    ? !!showRosterToTeamDate && now < showRosterToTeamDate.toDate()
    : !showRoster && !!teamSpecificShowDate && (
        !showRosterToTeamDate ||
        teamSpecificShowDate.seconds !== showRosterToTeamDate.seconds
      );

  return {
    showRosterSetLine, rosterSetDate, rosterSetDeadline,
    showVisibilityLine, visibilityDate, isVisibleToMembers, visibilityMaxDate,
  };
}

// buildAutoFillPairings moved to teeSheetAutoFill.ts, alongside the other tee-sheet auto-fill methods.
